import { useState, useRef } from "react";
import { Wrench, Loader2, Upload, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SURFACE_TYPES = [
  "cloison_verre",
  "fenetre",
  "baie_vitree",
  "vitrine",
  "porte_verre",
  "salle_reunion",
  "autre",
];

export const InstallationQuote = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "CH",
    surface_type: "cloison_verre",
    surface_m2: "",
    message: "",
  });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const fileInputRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error(t("installQuote.errorPhotoSize"));
      return;
    }
    setPhoto(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      const { data } = await axios.post(`${API}/install-quote`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.success) {
        setSent(true);
        toast.success(t("installQuote.success"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("installQuote.error"));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <section
        id="devis-pose"
        data-testid="install-quote-section"
        className="py-20 sm:py-28 bg-[#0d0c0b]"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto mb-6 border border-[#b87333]/40 bg-[#b87333]/10 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-[#d89557]" strokeWidth={1.5} />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#f4ede0] mb-4">
            {t("installQuote.successTitle")}
          </h2>
          <p className="text-sm text-[#cbc1ad] leading-relaxed">
            {t("installQuote.successDesc")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="devis-pose"
      data-testid="install-quote-section"
      className="py-20 sm:py-28 bg-[#0d0c0b] relative overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] hairline" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12">
        <header className="mb-10 sm:mb-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#b87333]/30 bg-[#b87333]/5 mb-5">
            <Wrench className="w-3.5 h-3.5 text-[#d89557]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#d89557]">
              {t("installQuote.badge")}
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#f4ede0] leading-[1.1]">
            {t("installQuote.title1")}{" "}
            <span className="copper-text italic">{t("installQuote.title2")}</span>
          </h2>
          <p className="mt-5 text-sm sm:text-base text-[#cbc1ad] max-w-2xl mx-auto leading-relaxed">
            {t("installQuote.intro")}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs text-[#d89557]">
            <MapPin className="w-3.5 h-3.5" />
            <span className="uppercase tracking-[0.22em]">{t("installQuote.zone")}</span>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Coordonnées */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("installQuote.name")} required>
              <input type="text" required value={form.name} onChange={set("name")} data-testid="iq-name" className={inputClass} />
            </Field>
            <Field label={t("installQuote.phone")} required>
              <input type="tel" required value={form.phone} onChange={set("phone")} data-testid="iq-phone" className={inputClass} placeholder="+41 …" />
            </Field>
          </div>
          <Field label={t("installQuote.email")} required>
            <input type="email" required value={form.email} onChange={set("email")} data-testid="iq-email" className={inputClass} />
          </Field>

          {/* Adresse chantier */}
          <div className="pt-3 border-t border-[#2a2422]">
            <div className="text-xs uppercase tracking-[0.22em] text-[#d89557] mb-3">
              {t("installQuote.addressTitle")}
            </div>
            <Field label={t("installQuote.address")} required>
              <input type="text" required value={form.address} onChange={set("address")} data-testid="iq-address" className={inputClass} />
            </Field>
            <div className="grid sm:grid-cols-3 gap-4 mt-4">
              <Field label={t("installQuote.postalCode")} required>
                <input type="text" required value={form.postal_code} onChange={set("postal_code")} data-testid="iq-postal" className={inputClass} />
              </Field>
              <Field label={t("installQuote.city")} required>
                <input type="text" required value={form.city} onChange={set("city")} data-testid="iq-city" className={inputClass} />
              </Field>
              <Field label={t("installQuote.country")} required>
                <select value={form.country} onChange={set("country")} data-testid="iq-country" className={inputClass}>
                  <option value="CH">🇨🇭 Suisse</option>
                  <option value="FR">🇫🇷 France voisine</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Projet */}
          <div className="pt-3 border-t border-[#2a2422]">
            <div className="text-xs uppercase tracking-[0.22em] text-[#d89557] mb-3">
              {t("installQuote.projectTitle")}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("installQuote.surfaceType")} required>
                <select value={form.surface_type} onChange={set("surface_type")} data-testid="iq-surface-type" className={inputClass}>
                  {SURFACE_TYPES.map((key) => (
                    <option key={key} value={key}>
                      {t(`installQuote.surfaceTypes.${key}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("installQuote.surfaceEstimate")}>
                <input type="number" step="0.1" min="0" value={form.surface_m2} onChange={set("surface_m2")} data-testid="iq-surface-m2" className={inputClass} placeholder="ex: 12.5" />
              </Field>
            </div>
          </div>

          {/* Photo */}
          <div className="pt-3 border-t border-[#2a2422]">
            <label className="block text-xs uppercase tracking-[0.22em] text-[#d89557] mb-3">
              {t("installQuote.photoLabel")}
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border border-dashed border-[#2a2422] hover:border-[#b87333]/60 bg-[#0a0a0a] p-6 cursor-pointer transition flex items-center gap-4"
            >
              <Upload className="w-5 h-5 text-[#d89557] flex-shrink-0" strokeWidth={1.5} />
              <div className="text-sm">
                <div className="text-[#f4ede0]">
                  {photo ? photo.name : t("installQuote.photoPlaceholder")}
                </div>
                <div className="text-xs text-[#7a716a] mt-1">
                  {t("installQuote.photoHint")}
                </div>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhoto}
              data-testid="iq-photo"
              className="hidden"
            />
          </div>

          {/* Message */}
          <Field label={t("installQuote.messageLabel")}>
            <textarea
              rows={4}
              value={form.message}
              onChange={set("message")}
              data-testid="iq-message"
              placeholder={t("installQuote.messagePlaceholder")}
              className={inputClass}
            />
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="iq-submit"
            className="btn-copper w-full py-4 text-sm uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            {t("installQuote.cta")}
          </button>

          <p className="text-[10px] text-center text-[#7a716a] uppercase tracking-[0.25em]">
            {t("installQuote.footer")}
          </p>
        </form>
      </div>
    </section>
  );
};

const inputClass =
  "w-full bg-[#0a0a0a] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-sm text-[#f4ede0] placeholder:text-[#7a716a] transition";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.18em] text-[#cbc1ad] mb-2">
      {label} {required && <span className="text-[#d89557]">*</span>}
    </span>
    {children}
  </label>
);
